import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, firstValueFrom, of } from 'rxjs';

interface I18nVersionManifest {
  generatedAt?: string;
  buildId?: string;
  languages?: Record<string, string>;
}

@Injectable({
  providedIn: 'root',
})
export class I18nVersionService {
  private readonly STORAGE_KEY = 'RGS-DESKTOP-I18N-VERSION-MANIFEST';

  private manifest: I18nVersionManifest = this.getInitialManifest();

  private readonly rawHttpClient: HttpClient;

  constructor() {
    const httpBackend = inject(HttpBackend);

    this.rawHttpClient = new HttpClient(httpBackend); // Bypass interceptors to avoid caching issues.
  }

  public async load() {
    const manifest = await firstValueFrom(
      this.rawHttpClient
        .get<I18nVersionManifest>(`i18n-version.json?v=${Date.now()}`)
        .pipe(catchError(() => of(undefined))),
    );

    if (!manifest) {
      return;
    }

    this.manifest = {
      generatedAt: manifest.generatedAt,
      buildId: manifest.buildId ?? this.manifest.buildId,
      languages: manifest.languages ?? {},
    };

    this.persistManifest();
  }

  public getVersionForLanguage(language: string) {
    return this.manifest.languages?.[language] ?? this.manifest.buildId ?? Date.now().toString();
  }

  private getInitialManifest() {
    const fallbackManifest: I18nVersionManifest = {
      buildId: Date.now().toString(),
      languages: {},
    };

    try {
      const storedManifest = localStorage.getItem(this.STORAGE_KEY);

      if (!storedManifest) {
        return fallbackManifest;
      }

      const parsedManifest = JSON.parse(storedManifest) as I18nVersionManifest;

      return {
        generatedAt: parsedManifest.generatedAt,
        buildId: parsedManifest.buildId ?? fallbackManifest.buildId,
        languages: parsedManifest.languages ?? {},
      };
    } catch {
      return fallbackManifest;
    }
  }

  private persistManifest() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.manifest));
    } catch {}
  }
}
